import { Component, ElementRef, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Result } from '@causality/core';
import { FeedbackForm } from '@fmnts/api/next';
import { WINDOW } from '@fmnts/common/browser';
import { logger } from '@fmnts/common/log';
import { FmntsProgressIndicatorModule } from '@fmnts/components/progress-indicator';
import { isRecord, isString } from '@fmnts/core';
import {
  DeclarativeFormComponent,
  ValidationStrategy,
} from '@fmnts/declarative-forms';
import { scrollToFirstErrorMessage } from '@fmnts/declarative-forms/common';
import {
  I18nModule,
  Locale,
  LocaleService,
  TranslationService,
} from '@fmnts/i18n';
import {
  SupporterFeedbackFormRepository,
  SupporterFeedbackFormSaveError,
  SupporterFeedbackFormUseCases,
  SupporterFeedbackResolveResult,
} from '@formunauts/supporter-feedback/data-access';
import { SupporterFeedbackLanguageSource } from '@formunauts/supporter-feedback/domain';
import * as Fn from 'effect/Function';
import * as Match from 'effect/Match';
import { EMPTY, catchError, finalize, map, switchMap, tap } from 'rxjs';
import {
  FormActionEvent,
  FormPageComponent,
} from './components/form-page/form-page.component';

enum ActionKeys {
  Save = 'submitForm',
}

@Component({
  selector: 'supporter-feedback-form',
  templateUrl: './supporter-feedback-form.component.html',
  styleUrls: ['./supporter-feedback-form.component.scss'],
  imports: [FormPageComponent, I18nModule, FmntsProgressIndicatorModule],
})
export class SupporterFeedbackFormComponent {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #translationService = inject(TranslationService);
  readonly #localeService = inject(LocaleService);
  readonly #el = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #window = inject(WINDOW);
  readonly #log = logger({ name: 'SupporterFeedbackFormComponent' });

  #useCase = {
    save: SupporterFeedbackFormUseCases.save(
      inject(SupporterFeedbackFormRepository),
    ),
  };

  protected readonly isSaving = signal<boolean>(false);
  protected readonly validationStrategy = ValidationStrategy.OnChange;

  public donorFeedback!: FeedbackForm;
  public hash!: string;

  constructor() {
    const {
      queryParams: { hash },
      data,
    } = this.#route.snapshot;

    const { error, feedbackForm } = data[
      'donorFeedbackResult'
    ] as SupporterFeedbackResolveResult;

    if (error || feedbackForm === undefined) {
      void this.#router.navigate(['not-found'], { relativeTo: this.#route });
    } else {
      this.hash = hash as string;
      this.donorFeedback = feedbackForm;

      this.#translationService.setLanguages({
        source: SupporterFeedbackLanguageSource.DonorFeedback,
        languages: [this.donorFeedback.language],
      });
      this.#localeService.setLocaleId(
        Locale.coerceLegacyLocaleId(this.donorFeedback.locale),
      );

      // TODO: Use template service or update in @fmnts/api
      // substitute fundraiser_name in labels to actual fundraiser
      const replaceFundraiserName = (value: string) =>
        value.replace(
          /({{([a-z]*_)*[a-z]*}})+/g,
          this.donorFeedback.fundraiserName,
        );
      feedbackForm.form.fieldsets[0].fields.forEach((element) => {
        const { label } = element;
        if (isString(label)) {
          element.label = replaceFundraiserName(label);
        } else if (isRecord(label)) {
          Object.keys(label).forEach((language) => {
            label[language] = replaceFundraiserName(label[language]);
          });
        }
      });
    }
  }

  handleAction(formActionEvent: FormActionEvent): void {
    const { action, formComponent } = formActionEvent;
    switch (action) {
      case ActionKeys.Save:
        return this.onSubmit(formComponent);
      default:
        this.#log.error(`Unknown action: ${action}`);
    }
  }

  onSubmit(form: DeclarativeFormComponent): void {
    // prevent multiple submissions
    if (this.isSaving()) {
      return;
    }

    form
      .submit$()
      .pipe(
        catchError(() => {
          scrollToFirstErrorMessage(this.#el.nativeElement);
          this.#log.info('the form is not valid');
          return EMPTY;
        }),
        map((formData) => this.formatValues(formData)),
        tap(() => {
          this.isSaving.set(true);
        }),
        switchMap((formatted) =>
          this.#useCase.save({ hash: this.hash, data: formatted }),
        ),
        finalize(() => {
          this.isSaving.set(false);
        }),
      )
      .subscribe((result) => {
        if (shouldReloadAfterSubmit(result)) {
          // reload to show result summary page
          this.#window.location.reload();
        }
      });
  }

  /**
   * Transforms `formData` for submission.
   *
   * @param formData Data from form
   *
   * @returns
   * Transformed form data
   */
  public formatValues(
    formData: Record<string, unknown>,
  ): Record<string, unknown> {
    const formattedValues: Record<string, unknown> = {
      ...formData,
      hash: this.donorFeedback.hash,
    };

    if ('compliments' in formattedValues) {
      formattedValues['compliments'] = transformArray(
        formattedValues['compliments'],
      );
    }
    if ('quality_feedback' in formattedValues) {
      formattedValues['quality_feedback'] = transformArray(
        formattedValues['quality_feedback'],
      );
    }

    return formattedValues;
  }
}

function transformArray(value: unknown): unknown {
  if (value === null) {
    return [];
  }

  return value;
}

const shouldReloadAfterSubmit = Result.match({
  onSuccess: Fn.constTrue,
  onFailure: Match.type<SupporterFeedbackFormSaveError>().pipe(
    Match.tag('SupporterFeedbackFormConflictError', Fn.constTrue),
    Match.orElse(Fn.constFalse),
  ),
});
